import {
  Avatar,
  Button,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Alert,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Container from "@mui/material/Container";
import InputLabel from "@mui/material/InputLabel";
import { Editor } from "@tinymce/tinymce-react";
import cx from "classnames";
import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import ImageUpload from "./ImageUpload";
import Loader from "./loader/Loader";
import NotesService from "./services/notesService";
import { SnackbarContext } from "./SnackbarProvider";

const initialNoteState = {
  category: "Single malt",
  name: "",
  abv: 40,
  age: "",
  score: 0,
  nose: 70,
  taste: 70,
  finish: 70,
  content: "",
  tags: [],
  public: false,
  image: null,
};

export const shortNoteCreator = (note, creationDate, modificationDate, id) => ({
  id,
  score: note.score,
  abv: note.abv,
  age: note.age,
  name: note.name,
  image: note.image,
  created: creationDate,
  modified: modificationDate,
});

const numericInputProps = {
  inputProps: {
    min: 0,
    max: 100,
    style: {
      paddingRight: 4,
    },
  },
};

const average = (list) =>
  list.reduce((prev, curr) => prev + curr) / list.length;

const isBetween = (num, min, max) =>
  num > Math.min.apply(Math, [min, max]) &&
  num < Math.max.apply(Math, [min, max]);

const AddNote = () => {
  const [note, setNote] = useState(initialNoteState);
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editorKey, setEditorKey] = useState(1);
  const history = useHistory();
  const { openErrorSnack, openSuccessSnack } = useContext(SnackbarContext);
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      NotesService.getByID(id)
        .then((docRef) => {
          setNote(docRef.data());
          setIsLoading(false);
        })
        .catch(() => {
          openErrorSnack("An error occured during getting note by id");
        });
    }
  }, [id, openErrorSnack]);

  const submitForm = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    try {
      if (id) {
        await NotesService.update(id, { ...note });
        await NotesService.updateShort(
          shortNoteCreator(note, note.createdAt.seconds * 1000, Date.now(), id)
        );
      } else {
        const newNote = await NotesService.create(note);
        await NotesService.updateShort(
          shortNoteCreator(note, Date.now(), null, newNote.id)
        );
      }
      history.push("/");
      openSuccessSnack("Note saved!");
    } catch (error) {
      console.log("error ", error);
      openErrorSnack("An error occured suring submitting form");
    }
    setNote(initialNoteState);
    setIsLoading(false);
    setEditorKey(editorKey * 2); // weird, but working way to clear the editor
  };

  useEffect(() => {
    const result =
      ["Single malt", "Single cask", "Blended malt", "Blend"].includes(
        note.category
      ) &&
      note.name.length > 0 &&
      note.content.length > 0 &&
      isBetween(note.abv, 30, 80) &&
      isBetween(note.nose, 0, 100) &&
      isBetween(note.taste, 0, 100) &&
      isBetween(note.finish, 0, 100) &&
      isBetween(note.score, 0, 100);

    setIsValid(result);
  }, [note]);

  useEffect(() => {
    const score = Math.round(
      average([
        parseInt(note.nose, 10),
        parseInt(note.taste, 10),
        parseInt(note.finish, 10),
      ])
    );

    setNote((n) => ({ ...n, score }));
  }, [note.nose, note.finish, note.taste]);

  const handleChange = (e) => {
    let { value, name, checked } = e.target;

    if (name === "public") {
      setNote({ ...note, public: checked });
    } else if (["nose", "taste", "finish"].includes(name)) {
      setNote({ ...note, [name]: parseInt(value, 10) });
    } else if (["abv"].includes(name)) {
      setNote({ ...note, [name]: parseFloat(value, 10) });
    } else {
      setNote({ ...note, [name]: value });
    }
  };

  const onTagClick = (clickedTag) => {
    if (note.tags.includes(clickedTag)) {
      setNote({ ...note, tags: note.tags.filter((x) => x !== clickedTag) });
    } else {
      setNote({ ...note, tags: [...note.tags, clickedTag] });
    }
  };

  const handleEditorChange = (content, editor) => {
    setNote({ ...note, content });
  };

  const imageSetCallback = useCallback(
    (url) => setNote((n) => ({ ...n, image: url })),
    []
  );

  const useStyles = makeStyles((theme) => ({
    root: {
      display: "flex",
      flexWrap: "wrap",
      width: "100%",
      marginBottom: "40px",
      marginTop: "40px",
    },
    formControl: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    formControlCategory: {
      width: "150px",
    },
    formControlShort: {
      width: "95px",
    },
    formControlNumber: {
      width: "70px",
    },
    saveButton: {
      marginTop: "40px",
    },
    formRow: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
    },
    avatar: {
      textAlign: "center",
      fontSize: "0.8rem",
      display: "flex",
      alignItems: "center",
    },
    tag: {
      margin: theme.spacing(0.5),
    },
    // !important needed to force color on ":focus state"
    selectedTag: {
      backgroundColor: "#354D9F !important",
      color: "#fff",
    },
    loader: {
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(8),
    },
  }));

  const classes = useStyles();

  // tasting tags  https://www.whiskybase.com/whiskies/whisky/165042/ballantines-07-year-old  (https://material-ui.com/components/chips/)
  // https://www.shutterstock.com/image-vector/whisky-tasting-notes-template-describe-profile-383735752
  // http://humphreyswhiskyclub.weebly.com/tasting-notes.html
  // https://www.whiskybase.com/whiskies/whisky/181551/johnnie-walker-black-label
  // Ciastko (1) Cytrusowy (6) Drewno (1) Dymny (4) Korzenny (1) Kwiatowy (2) Medyczny (1) Miodowy (2) Morski (3) Owocowy (6) Pikantny (3) Sherry (2) Skórzany (1) Suszony owoc (2) Torfowy (4) Waniliowy (3) Zbożowy (2) Inne cechy

  const tags = [
    "Sherry, port, wine",
    "Berries, dried fruits",
    "Apple, pear, peach",
    "Citrus",
    "Floral",
    "Vanilla",
    "Caramel",
    "Malt",
    "Nuts",
    "Oak, wood",
    "Coffee, chocolate",
    "Spices",
    "Leather, tobbaco",
    "Salt, sea, iodine",
    "Medicinal smoke",
    "Aromatic smoke",
  ];

  return (
    <Container maxWidth="md">
      {isLoading ? (
        <Grid container justifyContent="center" className={classes.loader}>
          <Grid item>
            <Loader />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={0} justifyContent="center">
          <Grid item xs={12} md={6}>
            <form
              className={classes.root}
              onSubmit={submitForm}
              autoComplete="off"
              noValidate
            >
              <div className={classes.formRow}>
                <FormControl
                  className={cx(classes.formControl)}
                  size="small"
                  fullWidth
                >
                  <TextField
                    label="Name"
                    name="name"
                    id="name-input"
                    onChange={handleChange}
                    value={note.name}
                    size="small"
                  />
                </FormControl>
              </div>
              <div className={classes.formRow}>
                <FormControl
                  size="small"
                  className={cx(classes.formControl, classes.formControlShort)}
                >
                  <TextField
                    size="small"
                    label="Age"
                    name="age"
                    id="age-input"
                    onChange={handleChange}
                    value={note.age}
                  />
                </FormControl>
                <FormControl
                  size="small"
                  className={cx(classes.formControl, classes.formControlShort)}
                >
                  <TextField
                    size="small"
                    label="ABV"
                    type="number"
                    name="abv"
                    id="abv-input"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                      inputProps: { min: 30, max: 80 },
                    }}
                    onChange={handleChange}
                    value={note.abv}
                  />
                </FormControl>
                <FormControl
                  size="small"
                  className={cx(
                    classes.formControl,
                    classes.formControlCategory
                  )}
                >
                  <InputLabel id="category-input">Category</InputLabel>
                  <Select
                    size="small"
                    name="category"
                    label="Category"
                    id="category-select"
                    onChange={handleChange}
                    value={note.category}
                  >
                    <MenuItem value="Single malt">Single malt</MenuItem>
                    <MenuItem value="Single cask">Single cask</MenuItem>
                    <MenuItem value="Blended malt">Blended malt</MenuItem>
                    <MenuItem value="Blend">Blend</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className={classes.formRow}>
                <FormControl
                  size="small"
                  className={cx(classes.formControl, classes.formControlNumber)}
                >
                  <TextField
                    size="small"
                    label="Nose"
                    type="number"
                    name="nose"
                    id="nose-input"
                    onChange={handleChange}
                    value={note.nose}
                    InputProps={numericInputProps}
                  />
                </FormControl>
                <FormControl
                  size="small"
                  className={cx(classes.formControl, classes.formControlNumber)}
                >
                  <TextField
                    size="small"
                    label="Taste"
                    type="number"
                    name="taste"
                    id="taste-input"
                    onChange={handleChange}
                    value={note.taste}
                    InputProps={numericInputProps}
                  />
                </FormControl>
                <FormControl
                  size="small"
                  className={cx(classes.formControl, classes.formControlNumber)}
                >
                  <TextField
                    size="small"
                    label="Finish"
                    type="number"
                    name="finish"
                    id="finish-input"
                    onChange={handleChange}
                    value={note.finish}
                    InputProps={numericInputProps}
                  />
                </FormControl>
                <div className={classes.avatar}>
                  <span style={{ marginRight: "7px" }}>Score</span>
                  <Avatar>{note.score}</Avatar>
                </div>
              </div>
              <FormControl className={classes.formControl} fullWidth>
                <Editor
                  value={note.content}
                  key={editorKey}
                  apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                  init={{
                    height: 300,
                    menubar: false,
                    plugins: [],
                    toolbar:
                      "bold italic forecolor backcolor | bullist numlist | removeformat",
                  }}
                  onEditorChange={handleEditorChange}
                />
              </FormControl>
              <div>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    clickable
                    className={`${classes.tag} ${
                      note.tags.includes(tag) ? classes.selectedTag : null
                    }`}
                    label={tag}
                    onClick={() => onTagClick(tag)}
                    variant="outlined"
                  />
                ))}
              </div>
              <div className={classes.formRow}>
                <ImageUpload onImageSet={imageSetCallback} />
              </div>
              {note.image ? (
                <div className={classes.formRow}>
                  <FormControl className={classes.formControl}>
                    <img src={note.image} alt="uploaded" />
                  </FormControl>
                </div>
              ) : null}
              <FormControl
                className={cx(classes.formControl, classes.saveButton)}
                fullWidth
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isLoading || !isValid}
                >
                  Save
                </Button>
              </FormControl>
              {/* TODO STYLE IT BETTER, MAKE MORE CLEVER ERRORS */}
              {!isValid ? (
                <Alert severity="error">Please fill form correctly</Alert>
              ) : null}
              {note.tags.length === 0 ? (
                <Alert severity="warning">Consider selecting taste tags</Alert>
              ) : null}
              {!note.image ? (
                <Alert severity="warning">Consider uploading photo</Alert>
              ) : null}
            </form>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default AddNote;

// TODO Distillery dropdown + map. Data available here: https://www.visitscotland.com/things-to-do/food-drink/whisky/distilleries/
